// src/components/process/MobileTreeTable.jsx
// 카드 기반 공정 트리 뷰

import React, { useState } from "react";
import { ChevronRight, ChevronDown, Activity, Clock, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import SidePanel from "../common/layout/SidePanel";
import { getActivityView } from "../../api/process";
import ActivityView from "./ActivityView";

// 하위 활동 수집 (집계용)
function collectActivities(node) {
  let acts = [...(node.activities || [])];
  (node.children || []).forEach((c) => {
    acts = acts.concat(collectActivities(c));
  });
  return acts;
}

export default function MobileTreeTable({ treeData = [] }) {
  const [openNodes, setOpenNodes] = useState(new Set());
  const [showOnlyCP, setShowOnlyCP] = useState(false);
  const [showOnlyCompleted, setShowOnlyCompleted] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [ganttData, setGanttData] = useState([]);

  const statusConfig = {
    NORMAL: { label: "-", icon: Activity, color: "bg-gray-100 text-gray-600" },
    DELAY: { label: "지연", icon: AlertTriangle, color: "bg-red-100 text-red-600" },
    DONE: { label: "완료", icon: CheckCircle2, color: "bg-green-100 text-green-600" },
    EARLY: { label: "선행", icon: TrendingUp, color: "bg-blue-100 text-blue-600" },
  };

  const toggleNode = (key) => {
    const next = new Set(openNodes);
    next.has(key) ? next.delete(key) : next.add(key);
    setOpenNodes(next);
  };

  const toggleAll = (expand) => {
    if (expand) {
      const collectKeys = (nodes) => {
        let keys = [];
        nodes.forEach((n) => {
          keys.push(n.key);
          keys = keys.concat(collectKeys(n.children || []));
        });
        return keys;
      };
      setOpenNodes(new Set(collectKeys(treeData)));
    } else {
      setOpenNodes(new Set());
    }
  };

  function filterNodes(nodes, showOnlyCP, showOnlyCompleted) {
    if (!showOnlyCP && !showOnlyCompleted) return nodes;
    return nodes
      .map((node) => {
        const filteredChildren = filterNodes(node.children || [], showOnlyCP, showOnlyCompleted);
        const filteredActs = (node.activities || []).filter((act) => {
          const matchesCP = !showOnlyCP || act.is_cp;
          const isCompleted = Number(act.percent_complete) >= 100 ||
            Number(act.actual_progress) >= 100 ||
            act.status === "DONE";
          const matchesCompleted = !showOnlyCompleted || isCompleted;
          return matchesCP && matchesCompleted;
        });
        if (filteredChildren.length > 0 || filteredActs.length > 0) {
          return { ...node, activities: filteredActs, children: filteredChildren };
        }
        return null;
      })
      .filter(Boolean);
  }

  const visibleData = filterNodes(treeData, showOnlyCP, showOnlyCompleted);

  const handleActivityClick = async (act) => {
    setSelectedActivity(act);
    setIsPanelOpen(true);
    try {
      const res = await getActivityView(act.id);
      setGanttData(res);
    } catch (err) {
      console.error("Activity 상세 로드 실패:", err);
    }
  };

  // 활동 카드 렌더링
  const renderActivityCard = (act, depth) => {
    const plan = Number(act.plan_labor) || 0;
    const actual = Number(act.actual_labor) || 0;
    const percent = plan > 0 ? Math.min(Math.round((actual / plan) * 100), 100) : 0;
    const displayPercent = !isNaN(percent) && percent > 0 ? percent : act.percent_complete || 0;

    const status = statusConfig[act.status] || statusConfig.NORMAL;
    const StatusIcon = status.icon;

    // 진행률에 따른 색상
    let progressColor = "bg-blue-500";
    if (act.status === "DELAY") progressColor = "bg-red-500";
    if (act.status === "DONE") progressColor = "bg-green-500";

    return (
      <div
        key={act.id}
        onClick={() => handleActivityClick(act)}
        className={`
          bg-white rounded-lg border p-3 mb-2 hover:bg-gray-50
          active:opacity-80 transition-all
          ${act.is_cp ? "border-l-4 border-l-red-500" : "border-gray-200"}
        `}
        style={{ marginLeft: `${(depth + 1) * 8}px` }}
      >
        {/* 상단: 이름 + 상태 뱃지 */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h4 className={`text-sm font-medium truncate ${act.is_cp ? "text-red-600" : "text-gray-900"}`}>
              {act.name}
            </h4>
            <p className="text-[11px] text-gray-400 mt-0.5">{act.activity_code}</p>
          </div>
          <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${status.color}`}>
            <StatusIcon className="w-3 h-3" />
            {status.label}
          </span>
        </div>

        {/* 중간: 진행률 바 */}
        <div className="mb-2">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-gray-500">진행률</span>
            <span className="font-semibold text-gray-700">{displayPercent}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
            <div
              className={`h-full rounded transition-all ${progressColor}`}
              style={{ width: `${displayPercent}%` }}
            />
          </div>
        </div>

        {/* 하단: 실적 정보 */}
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-1 text-gray-500">
            <Clock className="w-3 h-3" />
            <span>실적/계획</span>
          </div>
          <span className="font-medium text-gray-700">
            {Math.round(actual).toLocaleString()} / {Math.round(plan).toLocaleString()}
          </span>
        </div>

        {/* CP 표시 */}
        {act.is_cp && (
          <div className="mt-2 flex items-center gap-1 text-[10px] text-red-500">
            <AlertTriangle className="w-3 h-3" />
            <span>Critical Path</span>
            {act.total_float != null && (
              <span className="ml-auto">TF: {act.total_float}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  // WBS 그룹 카드 렌더링
  const renderGroupCard = (node, depth = 0) => {
    const isOpen = openNodes.has(node.key);
    const allActs = collectActivities(node);

    const totalPlan = allActs.reduce((sum, a) => sum + (Number(a.plan_labor) || 0), 0);
    const totalActual = allActs.reduce((sum, a) => sum + (Number(a.actual_labor) || 0), 0);
    const percent = totalPlan > 0 ? Math.min(Math.round((totalActual / totalPlan) * 100), 100) : 0;

    const depthColors = [
      "border-blue-400",
      "border-green-400",
      "border-orange-400",
      "border-gray-400",
    ];

    return (
      <div key={node.key} className="mb-2" style={{ marginLeft: `${depth * 8}px` }}>
        {/* 그룹 헤더 카드 */}
        <button
          onClick={() => toggleNode(node.key)}
          className={`
            w-full text-left rounded-lg overflow-hidden border-l-4
            bg-gray-100 hover:bg-gray-200
            ${depthColors[depth % depthColors.length]}
            active:opacity-80 transition-all
          `}
        >
          <div className="px-3 py-2">
            {/* 상단: 이름 + 화살표 */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                {isOpen ? (
                  <ChevronDown className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
                )}
                <span className="text-sm font-medium text-gray-800 truncate">
                  {node.name}
                </span>
              </div>
              <span className="text-xs text-gray-500 flex-shrink-0">
                {allActs.length}개
              </span>
            </div>

            {/* 진행률 바 */}
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 rounded overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded transition-all"
                  style={{ width: `${percent}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-gray-700 min-w-[36px] text-right">
                {percent}%
              </span>
            </div>
          </div>
        </button>

        {/* 펼쳐진 내용 */}
        {isOpen && (
          <div className="mt-2 pl-2 border-l-2 border-gray-200">
            {/* 활동 카드들 */}
            {node.activities.map((act) => renderActivityCard(act, depth))}

            {/* 하위 그룹 */}
            {node.children.map((child) => renderGroupCard(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* 상단 컨트롤 */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {/* 필터 체크박스 (CP / 완료) */}
        <div className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border shadow-sm">
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyCP}
              onChange={(e) => setShowOnlyCP(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-red-500 focus:ring-red-500"
            />
            <span className="text-xs font-medium text-gray-700">Critical Path만</span>
          </label>
          <label className="flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnlyCompleted}
              onChange={(e) => setShowOnlyCompleted(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-green-500 focus:ring-green-500"
            />
            <span className="text-xs font-medium text-gray-700">완료</span>
          </label>
        </div>

        {/* 펼치기/닫기 버튼 */}
        <div className="flex gap-2">
          <button
            onClick={() => toggleAll(true)}
            className="px-3 py-2 text-xs font-medium bg-blue-500 text-white rounded-lg shadow-sm active:bg-blue-600 transition-colors"
          >
            모두 펼치기
          </button>
          <button
            onClick={() => toggleAll(false)}
            className="px-3 py-2 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg shadow-sm active:bg-gray-200 transition-colors"
          >
            모두 닫기
          </button>
        </div>
      </div>

      {/* 데이터 없음 */}
      {visibleData.length === 0 && (
        <div className="bg-white rounded-xl p-8 text-center text-gray-500 shadow-sm">
          <Activity className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">표시할 공정 데이터가 없습니다.</p>
        </div>
      )}

      {/* 트리 카드 리스트 */}
      <div className="space-y-2">
        {visibleData.map((node) => renderGroupCard(node))}
      </div>

      {/* 상세 패널 */}
      <SidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        title={selectedActivity?.name}
        width={400}
        side="right"
      >
        {selectedActivity && (
          <ActivityView activity={selectedActivity} ganttData={ganttData} />
        )}
      </SidePanel>
    </div>
  );
}

